import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { updateProfile } from "firebase/auth";
import { useToast } from "@/hooks/use-toast";
import DashboardTopbar from "@/components/layout/DashboardTopbar";
import {
  FirmSettings,
  getFirmSettings,
  saveFirmSettings,
  updateFirmSettings,
  deleteFirmSettings
} from "@/lib/firebaseStorageService";
import { Loader2, Save, Settings as SettingsIcon } from "lucide-react";

const Settings = () => {
  const { currentUser } = useAuth();
  const { toast } = useToast();

  // Profile states
  const [name, setName] = useState(currentUser?.displayName || "");
  const [loading, setLoading] = useState(false);

  // Entity management states
  const [firmSettings, setFirmSettings] = useState<FirmSettings | null>(null);
  const [loadingEntities, setLoadingEntities] = useState({
    firmSettings: false
  });

  // Firm settings form states
  const [firmName, setFirmName] = useState("");
  const [firmRegNumber, setFirmRegNumber] = useState("");
  const [firmAddress, setFirmAddress] = useState("");
  const [firmContactEmail, setFirmContactEmail] = useState("");
  const [firmPhone, setFirmPhone] = useState("");
  const [firmPreferences, setFirmPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    autoBackup: true,
    dataRetentionPeriod: 12,
    defaultReportFormat: "PDF",
    theme: "light"
  });

  // Edit states
  const [savingFirmSettings, setSavingFirmSettings] = useState(false);

  // Profile management
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      return;
    }

    setLoading(true);
    try {
      await updateProfile(currentUser, {
        displayName: name
      });

      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully updated."
      });
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Failed to update profile. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };



  // Load entities on component mount
  useEffect(() => {
    if (currentUser) {
      fetchFirmSettings();
    }
  }, [currentUser]);

  // Fetch firm settings
  const fetchFirmSettings = async () => {
    if (!currentUser) return;

    setLoadingEntities(prev => ({ ...prev, firmSettings: true }));
    try {
      const settings = await getFirmSettings(currentUser.uid);
      if (settings) {
        setFirmSettings(settings);
        setFirmName(settings.firmName);
        setFirmRegNumber(settings.registrationNumber);
        setFirmAddress(settings.address || "");
        setFirmContactEmail(settings.contactEmail || "");
        setFirmPhone(settings.phone || "");
        if (settings.preferences) {
          setFirmPreferences(settings.preferences);
        }
      }
    } catch (error) {
      console.error("Error fetching firm settings:", error);
      toast({
        title: "Error",
        description: "Failed to load firm settings",
        variant: "destructive"
      });
    } finally {
      setLoadingEntities(prev => ({ ...prev, firmSettings: false }));
    }
  };


  // Handle firm settings
  const handleSaveFirmSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    setSavingFirmSettings(true);
    try {
      if (firmSettings) {
        // Update existing settings
        await updateFirmSettings(currentUser.uid, {
          firmName,
          registrationNumber: firmRegNumber,
          address: firmAddress,
          contactEmail: firmContactEmail,
          phone: firmPhone,
          preferences: firmPreferences
        });
      } else {
        // Create new settings
        await saveFirmSettings(
          currentUser.uid,
          firmName,
          firmRegNumber,
          firmAddress,
          firmContactEmail,
          firmPhone,
          firmPreferences
        );
      }

      await fetchFirmSettings();
      toast({
        title: "Success",
        description: "Firm settings saved successfully"
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save firm settings",
        variant: "destructive"
      });
    } finally {
      setSavingFirmSettings(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardTopbar />
      <div className="pt-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto p-6 space-y-8">
          <div className="flex items-center space-x-2">
            <SettingsIcon className="h-6 w-6" />
            <h1 className="text-3xl font-bold">Settings</h1>
          </div>

      <Tabs defaultValue="account" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="account">Account</TabsTrigger>
          <TabsTrigger value="firm">Firm Settings</TabsTrigger>
        </TabsList>

        {/* Account Tab */}
        <TabsContent value="account" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>View your personal information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={name}
                    readOnly
                    className="bg-gray-50"
                    placeholder="Your full name"
                  />
                  <p className="text-sm text-gray-500">Your name cannot be changed.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={currentUser?.email || ""}
                    readOnly
                    className="bg-gray-50"
                  />
                  <p className="text-sm text-gray-500">Your email cannot be changed.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          
        </TabsContent>



        {/* Firm Settings Tab */}
        <TabsContent value="firm" className="space-y-6">
          {loadingEntities.firmSettings ? (
            <Card>
              <CardContent className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2">Loading firm settings...</span>
              </CardContent>
            </Card>
          ) : (
            <form onSubmit={handleSaveFirmSettings} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Firm Information</CardTitle>
                  <CardDescription>Update your firm's basic information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firmName">Firm Name *</Label>
                      <Input
                        id="firmName"
                        value={firmName}
                        onChange={(e) => setFirmName(e.target.value)}
                        placeholder="Your firm name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="firmRegNumber">Registration Number *</Label>
                      <Input
                        id="firmRegNumber"
                        value={firmRegNumber}
                        onChange={(e) => setFirmRegNumber(e.target.value)}
                        placeholder="Registration number"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="firmAddress">Address</Label>
                    <Input
                      id="firmAddress"
                      value={firmAddress}
                      onChange={(e) => setFirmAddress(e.target.value)}
                      placeholder="Business address"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firmContactEmail">Contact Email</Label>
                      <Input
                        id="firmContactEmail"
                        type="email"
                        value={firmContactEmail}
                        onChange={(e) => setFirmContactEmail(e.target.value)}
                        placeholder="contact@firm.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="firmPhone">Phone</Label>
                      <Input
                        id="firmPhone"
                        value={firmPhone}
                        onChange={(e) => setFirmPhone(e.target.value)}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </form>
          )}
        </TabsContent>

      </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Settings;
